import {type PayloadAction, createSlice} from '@reduxjs/toolkit';
import {Concept} from 'types';
import type {Sorting} from 'components/dataTable/types';
import {type Locale, MasterLocale} from 'localization/types';
import type {ColumnConfig} from 'schema/table/types';
import type {CurrentFilterType} from 'services/advancedSearch/types';
import type {RootState} from 'store/types';
import {
  excludeBokbasenFilter,
  excludeInternationalFilter,
} from 'services/advancedSearch';
import {
  type FilterModel,
  type PaginationModel,
  type SettingsState,
} from './types';

export const initialState: SettingsState = {
  preferredLocale: MasterLocale,
  overview: {
    searchVisibility: false,
    searchText: '',
    selectedTaskType: 'new',
    columnsVisibility: {},
    columnsOrder: {},
    sorting: {
      field: 'time',
      sort: 'desc',
    },
  },
  agents: {
    searchVisibility: false,
    searchText: '',
    selectedAgentType: Concept.person,
    columnsVisibility: {},
    columnsOrder: {},
    sorting: {
      field: 'time',
      sort: 'desc',
    },
  },
  tables: {
    test: {
      sorting: undefined,
      columns: [
        {
          name: 'edition',
          visible: true,
        },
      ],
    },
  },
  advancedSearch: {
    currentFilters: [excludeBokbasenFilter, excludeInternationalFilter],
  },
  showHachette: false,
  mergeSuggestionList: {
    currentType: 'SERIES',
    showUserCreated: false,
  },
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    localeUpdated(state, action: PayloadAction<Locale>) {
      state.preferredLocale = action.payload;
    },
    tableColumnConfigUpdated(
      state,
      action: PayloadAction<{
        name: string;
        config: ColumnConfig[];
      }>,
    ) {
      if (state.tables[action.payload.name]) {
        state.tables[action.payload.name].columns = action.payload.config;
      } else {
        state.tables = {
          ...state.tables,
          [action.payload.name]: {
            columns: action.payload.config,
          },
        };
      }
    },
    tableSortingUpdated(
      state,
      action: PayloadAction<{
        name: string;
        sorting?: Sorting;
      }>,
    ) {
      if (state.tables[action.payload.name]) {
        state.tables[action.payload.name].sorting = action.payload.sorting;
      } else {
        state.tables = {
          ...state.tables,
          [action.payload.name]: {
            ...state.tables[action.payload.name],
            sorting: action.payload.sorting,
          },
        };
      }
    },
    tableFilterModelUpdated(
      state,
      action: PayloadAction<{
        name: string;
        filterModel?: FilterModel;
      }>,
    ) {
      if (state.tables[action.payload.name]) {
        state.tables[action.payload.name].filterModel =
          action.payload.filterModel;
      } else {
        state.tables = {
          ...state.tables,
          [action.payload.name]: {
            ...state.tables[action.payload.name],
            filterModel: action.payload.filterModel,
          },
        };
      }
    },
    tablePaginationModelUpdated(
      state,
      action: PayloadAction<{
        name: string;
        paginationModel?: PaginationModel;
      }>,
    ) {
      if (state.tables[action.payload.name]) {
        state.tables[action.payload.name].paginationModel =
          action.payload.paginationModel;
      } else {
        state.tables = {
          ...state.tables,
          [action.payload.name]: {
            ...state.tables[action.payload.name],
            paginationModel: action.payload.paginationModel,
          },
        };
      }
    },
    tablePaginationModelResetted(
      state,
      action: PayloadAction<{
        name: string;
        pageSize?: PaginationModel['pageSize'];
      }>,
    ) {
      if (state.tables[action.payload.name]) {
        state.tables[action.payload.name].paginationModel = {
          page: 0,
          pageSize:
            action.payload.pageSize ??
            state.tables[action.payload.name]?.paginationModel?.pageSize ??
            20,
        };
      } else {
        state.tables = {
          ...state.tables,
          [action.payload.name]: {
            ...state.tables[action.payload.name],
            paginationModel: {
              page: 0,
              pageSize: action.payload.pageSize ?? 20,
            },
          },
        };
      }
    },
    overviewColumnVisibilityUpdated(
      state,
      action: PayloadAction<SettingsState['overview']['columnsVisibility']>,
    ) {
      state.overview.columnsVisibility = action.payload;
    },
    overviewColumnOrderUpdated(
      state,
      action: PayloadAction<SettingsState['overview']['columnsOrder']>,
    ) {
      state.overview.columnsOrder = action.payload;
    },
    overviewSelectedTaskTypeUpdated(
      state,
      action: PayloadAction<SettingsState['overview']['selectedTaskType']>,
    ) {
      state.overview.selectedTaskType = action.payload;
    },
    overviewSortingUpdated(
      state,
      action: PayloadAction<SettingsState['overview']['sorting']>,
    ) {
      state.overview.sorting = action.payload;
    },
    overviewSearchTextUpdated(
      state,
      action: PayloadAction<SettingsState['overview']['searchText']>,
    ) {
      state.overview.searchText = action.payload;
    },
    advancedSearchFiltersUpdated(
      state,
      action: PayloadAction<CurrentFilterType[]>,
    ) {
      state.advancedSearch.currentFilters = action.payload;
    },
    settingsAgentsColumnVisibilityUpdated(
      state,
      action: PayloadAction<SettingsState['agents']['columnsVisibility']>,
    ) {
      state.agents.columnsVisibility = action.payload;
    },
    settingsAgentsSortingUpdated(
      state,
      action: PayloadAction<SettingsState['agents']['sorting']>,
    ) {
      state.agents.sorting = action.payload;
    },
    settingsAgentsColumnOrderUpdated(
      state,
      action: PayloadAction<SettingsState['agents']['columnsOrder']>,
    ) {
      state.agents.columnsOrder = action.payload;
    },
    settingsAgentsSearchTextUpdated(state, action: PayloadAction<string>) {
      state.agents.searchText = action.payload;
    },
    settingsAgentsSearchVisibilityUpdated(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.agents.searchVisibility = action.payload;
    },
    settingsAgentsSelectedTaskTypeUpdated(
      state,
      action: PayloadAction<SettingsState['agents']['selectedAgentType']>,
    ) {
      state.agents.selectedAgentType = action.payload;
    },
    showHacetteUpdated(
      state,
      action: PayloadAction<SettingsState['showHachette']>,
    ) {
      state.showHachette = action.payload;
    },
    mergeSuggestionTypeUpdated(
      state,
      action: PayloadAction<
        SettingsState['mergeSuggestionList']['currentType']
      >,
    ) {
      state.mergeSuggestionList.currentType = action.payload;
    },
    mergeSuggestionShowUserCreatedUpdated(
      state,
      action: PayloadAction<
        SettingsState['mergeSuggestionList']['showUserCreated']
      >,
    ) {
      state.mergeSuggestionList.showUserCreated = action.payload;
    },
  },
});

export const {
  localeUpdated,
  tableColumnConfigUpdated,
  tableSortingUpdated,
  tableFilterModelUpdated,
  tablePaginationModelUpdated,
  tablePaginationModelResetted,
  overviewColumnVisibilityUpdated,
  overviewColumnOrderUpdated,
  overviewSelectedTaskTypeUpdated,
  overviewSortingUpdated,
  overviewSearchTextUpdated,
  advancedSearchFiltersUpdated,
  showHacetteUpdated,
  settingsAgentsColumnVisibilityUpdated,
  settingsAgentsSortingUpdated,
  settingsAgentsColumnOrderUpdated,
  settingsAgentsSearchTextUpdated,
  settingsAgentsSearchVisibilityUpdated,
  settingsAgentsSelectedTaskTypeUpdated,
  mergeSuggestionTypeUpdated,
  mergeSuggestionShowUserCreatedUpdated,
} = settingsSlice.actions;

export const selectSettings = (state: RootState) => state.settings;
export const selectSettingsPreferredLocale = (state: RootState) =>
  state.settings.preferredLocale;
export const selectSettingsAdvancedSearchCurrentFilters = (state: RootState) =>
  state.settings.advancedSearch.currentFilters;

export const selectSettingsShowHachette = (state: RootState) =>
  state.settings.showHachette;

// ======= TABLES =======
export const selectSettingsTables = (state: RootState) => state.settings.tables;

// ======= AGENTS =======
export const selectSettingsAgentsColumnsVisibility = (state: RootState) =>
  state.settings.agents.columnsVisibility;
export const selectSettingsAgentsSearchVisibility = (state: RootState) =>
  state.settings.agents.searchVisibility;
export const selectSettingsAgentsSelectedAgentType = (state: RootState) =>
  state.settings.agents.selectedAgentType;
export const selectSettingsAgentsSearchText = (state: RootState) =>
  state.settings.agents.searchText;
export const selectSettingsAgentsColumnsOrder = (state: RootState) =>
  state.settings.agents.columnsOrder;
export const selectSettingsAgentsSorting = (state: RootState) =>
  state.settings.agents.sorting;

// ======= OVERVIEW =======
export const selectSettingsOverviewSelectedTaskType = (state: RootState) =>
  state.settings.overview.selectedTaskType;
export const selectSettingsOverviewSearchText = (state: RootState) =>
  state.settings.overview.searchText;

// ======= MERGE SUGGESTIONS =======
export const selectMergeSuggestionCurrentType = (state: RootState) =>
  state.settings.mergeSuggestionList.currentType;
export const selectMergeSuggestionShowUserCreated = (state: RootState) =>
  state.settings.mergeSuggestionList.showUserCreated;

export default settingsSlice;
